import { useEffect, type ReactNode } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Splitting from "splitting";
import { animate, inView } from "framer-motion";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import TestimonialCarousel from "@/components/feature/TestimonialCarousel";
import PinnedProcessSection from "@/components/feature/PinnedProcessSection";
import { useSmoothScroll, scrollTo } from "@/hooks/useSmoothScroll";

gsap.registerPlugin(ScrollTrigger);

export interface FeaturePanel {
  title: ReactNode;
  body?: ReactNode;
  image?: string;
  imageAlt?: string;
  ctaLabel?: string;
  ctaHref?: string;
  titleSize?: string;
  titleClassName?: string;
}

export interface Testimonial {
  quote: string;
  name: string;
  location: string;
}

export interface CaseStudy {
  title: string;
  text: string;
  image?: string;
  imageAlt?: string;
  href?: string;
  label?: string;
  linkLabel?: string;
}

export interface FaqItem {
  frage: string;
  antwort: string;
}

export interface CrossLink {
  title: string;
  text: string;
  image?: string;
  imageAlt?: string;
  href: string;
  label?: string;
  titleSize?: string;
}

export interface CtaText {
  headline: string;
  subtext?: string;
  buttonLabel?: string;
  buttonHref?: string;
  buttonExternal?: boolean;
  secondaryButtonLabel?: string;
  secondaryButtonHref?: string;
  secondaryButtonExternal?: boolean;
  variant?: "green" | "dark";
}

export interface StatItem {
  value: string;
  label: string;
}


export type Theme = "default" | "futuristic";

export interface LeistungsseiteTemplateProps {
  theme?: Theme;
  heroImage?: string;
  heroImageAlt?: string;
  label?: string;
  title?: ReactNode;
  subline?: string;
  leadText?: ReactNode;
  panels?: FeaturePanel[];
  trustLine?: string;
  testimonials?: Testimonial[];
  showTrustStar?: boolean;
  manifest?: ReactNode;
  caseStudy?: CaseStudy;
  extraSection?: ReactNode;
  preManifestSection?: ReactNode;
  postCtaSection?: ReactNode;
  faq?: FaqItem[];
  ctaText?: CtaText;
  crossLink?: CrossLink;
  stats?: StatItem[];
  packageSection?: {
    headline: string;
    subline?: string;
    items: string[];
    footnote?: string;
  };
  /** Wenn true: GSAP-Reveals werden deaktiviert und Framer-Motion-Reveals verwendet. */
  enableReveal?: boolean;
}


/* ───────────── Reveal helpers ───────────── */

function useSplitReveal(selector: string, enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const targets = Array.from(document.querySelectorAll<HTMLElement>(selector));
    if (!targets.length) return;
    const triggers: ScrollTrigger[] = [];

    targets.forEach((el) => {
      if (prefersReduced) {
        gsap.fromTo(
          el,
          { opacity: 0 },
          {
            opacity: 1,
            duration: 0.6,
            scrollTrigger: { trigger: el, start: "top 85%", toggleActions: "play none none none" },
          }
        );
        return;
      }
      const result = Splitting({ target: el, by: "words" });
      const words = result[0]?.words ?? [];
      if (!words.length) return;
      gsap.set(words, { opacity: 0, y: 20, display: "inline-block" });
      const tween = gsap.to(words, {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power3.out",
        stagger: 0.04,
        scrollTrigger: { trigger: el, start: "top 85%", toggleActions: "play none none none" },
      });
      if (tween.scrollTrigger) triggers.push(tween.scrollTrigger);
    });

    return () => triggers.forEach((t) => t.kill());
  }, [selector, enabled]);
}

function useImageReveal(selector: string, enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    const imgs = Array.from(document.querySelectorAll<HTMLElement>(selector));
    imgs.forEach((img) => {
      gsap.fromTo(
        img,
        { opacity: 0, scale: 1.05 },
        {
          opacity: 1,
          scale: 1,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: img,
            start: "top 90%",
            toggleActions: "play none none none",
            onEnter: () => img.classList.add("is-inview"),
          },
        }
      );
    });
  }, [selector, enabled]);
}

/* Framer-Motion Reveal (nur wenn enableReveal=true) — überschreibt GSAP. */
function useFramerReveal(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const stops: Array<() => void> = [];
    const margin = "0px 0px -20% 0px";
    const ease = [0.16, 1, 0.3, 1] as const;

    const setInitialUp = (el: HTMLElement) => {
      el.style.opacity = "0";
      if (!reduce) el.style.transform = "translateY(40px)";
      el.style.willChange = "opacity, transform";
    };
    const setInitialRight = (el: HTMLElement) => {
      el.style.opacity = "0";
      if (!reduce) el.style.transform = "translateX(60px)";
      el.style.willChange = "opacity, transform";
    };

    const revealUp = (el: HTMLElement, delay = 0) => {
      setInitialUp(el);
      const stop = inView(
        el,
        () => {
          if (reduce) {
            animate(el, { opacity: 1 }, { duration: 0.3 });
          } else {
            animate(el, { opacity: 1, y: 0 }, { duration: 0.6, delay, ease });
          }
        },
        { margin }
      );
      stops.push(stop);
    };

    const revealRight = (el: HTMLElement) => {
      setInitialRight(el);
      const stop = inView(
        el,
        () => {
          if (reduce) {
            animate(el, { opacity: 1 }, { duration: 0.3 });
          } else {
            animate(el, { opacity: 1, x: 0 }, { duration: 0.7, ease });
          }
        },
        { margin }
      );
      stops.push(stop);
    };

    // Headlines (+ direkt folgender Body-Text mit Verzögerung)
    document.querySelectorAll<HTMLElement>("[data-split-reveal]").forEach((el) => {
      revealUp(el, 0);
      const next = el.nextElementSibling as HTMLElement | null;
      if (
        next &&
        (next.tagName === "P" || next.tagName === "DIV" || next.tagName === "FIGCAPTION") &&
        !next.hasAttribute("data-image-reveal") &&
        !next.querySelector("[data-image-reveal]")
      ) {
        revealUp(next, 0.1);
      }
    });

    // Bilder (Sticky-Panels, Case-Study, Cross-Link)
    document.querySelectorAll<HTMLElement>("[data-image-reveal]").forEach((el) => {
      revealRight(el);
    });

    return () => {
      stops.forEach((s) => s());
    };
  }, [enabled]);
}

/* ───────────── Theme tokens ───────────── */

function getTokens(theme: Theme) {
  if (theme === "futuristic") {
    return {
      page: "bg-[#0A0A0A]",
      sectionLead: "bg-[#0D1F12]",
      sectionPanels: "bg-[#0A0A0A]",
      sectionProof: "bg-[#0D1F12]",
      sectionManifest: "bg-[#0A0A0A]",
      sectionCase: "bg-[#0D1F12]",
      sectionFaq: "bg-[#0A0A0A]",
      sectionCross: "bg-[#0D1F12]",
      heading: "text-white",
      headingGlow: { textShadow: "0 0 28px rgba(76,175,80,0.45)" } as React.CSSProperties,
      body: "text-white/70",
      muted: "text-white/40",
      card: "bg-white/[0.03] border border-[#4CAF50]/25 rounded-2xl",
      cardImg: "bg-[#0A0A0A] border border-[#4CAF50]/20 rounded-2xl",
      divider: "divide-[#4CAF50]/15 border-[#4CAF50]/15",
      faqQuestion: "text-white",
      buttonPrimary:
        "btn-slide btn-fill-white bg-[#4CAF50] text-[#0A0A0A] shadow-[0_0_28px_rgba(76,175,80,0.45)]",
      buttonGhost:
        "btn-slide btn-fill-white bg-transparent text-white border border-[#4CAF50]/50 hover:shadow-[0_0_24px_rgba(76,175,80,0.35)]",
      accent: "text-[#4CAF50]",
      heroOverlay: "bg-[rgba(13,31,18,0.7)]",
    };
  }
  return {
    page: "bg-white",
    sectionLead: "bg-[#0A0A0A]",
    sectionPanels: "bg-white",
    sectionProof: "bg-[#F4F4F4]",
    sectionManifest: "bg-[#0A0A0A]",
    sectionCase: "bg-white",
    sectionFaq: "bg-white",
    sectionCross: "bg-[#F4F4F4]",
    heading: "text-[#0A0A0A]",
    headingGlow: {} as React.CSSProperties,
    body: "text-foreground-600",
    muted: "text-foreground-500",
    card: "bg-[#F4F4F4] rounded-2xl",
    cardImg: "bg-[#F4F4F4] rounded-2xl",
    divider: "divide-[#0A0A0A]/10 border-[#0A0A0A]/10",
    faqQuestion: "text-[#0A0A0A]",
    buttonPrimary: "btn-slide btn-fill-white bg-[#0A0A0A] text-white",
    buttonGhost: "btn-slide btn-fill-white bg-white text-[#0A0A0A] border border-[#0A0A0A]/10",
    accent: "text-[#4CAF50]",
    heroOverlay: "bg-gradient-to-t from-black/85 via-black/35 to-black/10",
  };
}

/* ───────────── Component ───────────── */

export default function LeistungsseiteTemplate({
  theme = "default",
  heroImage,
  heroImageAlt = "",
  label,
  title,
  subline,
  leadText,
  panels = [],
  trustLine,
  testimonials,
  showTrustStar = true,
  manifest,
  caseStudy,
  extraSection,
  preManifestSection,
  postCtaSection,
  faq = [],
  ctaText,
  crossLink,
  stats,
  packageSection,
  enableReveal = false,
}: LeistungsseiteTemplateProps) {

  useSmoothScroll();
  useSplitReveal("[data-split-reveal]", !enableReveal);
  useImageReveal("[data-image-reveal]", !enableReveal);
  useFramerReveal(enableReveal);

  const t = getTokens(theme);
  const isFuturistic = theme === "futuristic";

  // CTA panel colors
  const ctaIsGreen = ctaText?.variant !== "dark";
  const ctaBg = ctaIsGreen ? "#4CAF50" : "#0A0A0A";
  const ctaHeadingStyle: React.CSSProperties = {
    letterSpacing: "-2.4px",
    ...(isFuturistic && !ctaIsGreen ? { textShadow: "0 0 32px rgba(76,175,80,0.55)" } : {}),
  };
  const ctaButtonClass = isFuturistic
    ? ctaIsGreen
      ? "btn-slide btn-fill-white bg-[#0A0A0A] text-white shadow-[0_0_28px_rgba(0,0,0,0.4)]"
      : "btn-slide btn-fill-white bg-[#4CAF50] text-[#0A0A0A] shadow-[0_0_28px_rgba(76,175,80,0.5)]"
    : ctaIsGreen
      ? "btn-slide btn-fill-white bg-[#0A0A0A] text-white"
      : "btn-slide btn-fill-white bg-[#4CAF50] text-white";

  return (
    <div className={`min-h-screen ${t.page}`} style={{ fontFamily: "Archivo, sans-serif" }}>
      <Header />

      <main id="main-content">
        {/* 1. FULLSCREEN HERO */}
        <section className="relative h-screen min-h-[600px] w-full overflow-hidden">
          {heroImage ? (
            <img
              src={heroImage}
              alt={heroImageAlt}
              loading="lazy"
              data-image-reveal
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-[#0A0A0A]" />
          )}
          <div className={`absolute inset-0 ${t.heroOverlay}`} />
          {isFuturistic && (
            <div
              className="absolute inset-0 pointer-events-none opacity-40"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(76,175,80,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(76,175,80,0.08) 1px, transparent 1px)",
                backgroundSize: "48px 48px",
              }}
            />
          )}
          <div className="relative z-10 h-full flex flex-col justify-end px-6 md:px-10 lg:px-14 pb-16 md:pb-20">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 max-w-7xl mx-auto w-full">
              <div className="max-w-3xl">
                {label && (
                  <p className="text-[#4CAF50] uppercase text-xs md:text-sm tracking-widest mb-4 font-medium">
                    {label}
                  </p>
                )}
                {title && (
                  <h1
                    data-split-reveal
                    className="font-bold uppercase text-white leading-[0.95]"
                    style={{
                      fontSize: "clamp(1.5rem, 5.5vw, 3.5rem)",
                      letterSpacing: "clamp(-0.5px, -0.12vw, -2px)",
                      ...(isFuturistic ? { textShadow: "0 0 36px rgba(76,175,80,0.35)" } : {}),
                    }}
                  >
                    {title}
                  </h1>
                )}
                {subline && (
                  <p className="text-white/85 text-base md:text-lg mt-5 max-w-xl leading-relaxed">
                    {subline}
                  </p>
                )}
              </div>
              <button
                onClick={() => scrollTo("#leistung-panels", { offset: -40 })}
                className={`self-start md:self-end inline-flex items-center gap-2 px-8 py-4 rounded-full text-sm font-medium transition-all whitespace-nowrap ${t.buttonGhost}`}
              >
                So funktioniert's ↓
              </button>
            </div>
          </div>
        </section>

        {/* 2. LEAD-TEXT-BLOCK */}
        {leadText && (
          <section className={`${t.sectionLead} py-36 md:py-40 px-6 md:px-10 lg:px-14`}>
            <div className="max-w-7xl mx-auto flex justify-end">
              <div
                data-split-reveal
                className="text-white text-xl md:text-2xl lg:text-3xl leading-[1.4] text-right md:w-[60%]"
              >
                {leadText}
              </div>
            </div>
          </section>
        )}

        {/* 2b. STATS BAND */}
        {stats && stats.length > 0 && (
          <section className={`${t.sectionLead} pb-24 md:pb-28 px-6 md:px-10 lg:px-14 -mt-20 md:-mt-24`}>
            <div className="max-w-7xl mx-auto">
              <div className={`flex flex-col md:flex-row md:items-stretch md:divide-x ${isFuturistic ? "divide-[#4CAF50]/20" : "divide-white/15"} border-y ${isFuturistic ? "border-[#4CAF50]/20" : "border-white/15"} py-10 md:py-12 gap-8 md:gap-0`}>
                {stats.map((s, i) => (
                  <div key={i} data-split-reveal className="flex-1 md:px-10 text-center md:text-left">
                    <div
                      className="text-[#4CAF50] font-bold leading-none"
                      style={{
                        fontSize: "clamp(1.375rem, 4.5vw, 3.25rem)",
                        letterSpacing: "-1.5px",
                        ...(isFuturistic ? { textShadow: "0 0 24px rgba(76,175,80,0.45)" } : {}),
                      }}
                    >
                      {s.value}
                    </div>
                    <div className="mt-3 text-white/70 text-sm md:text-base uppercase tracking-widest">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}



        {/* 3. STICKY FEATURE-PANELS */}
        {panels.length > 0 && (
          <PinnedProcessSection
            panels={panels}
            t={{
              sectionPanels: t.sectionPanels,
              heading: t.heading,
              headingGlow: t.headingGlow,
              body: t.body,
              muted: t.muted,
              cardImg: t.cardImg,
              buttonPrimary: t.buttonPrimary,
              accent: t.accent,
            }}
          />
        )}

        {/* 3b. PACKAGE / ALLES IM PAKET */}
        {packageSection && (
          <section className="bg-[#F4F4F4] py-36 md:py-40 px-6 md:px-10 lg:px-14">
            <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
              <div className="md:col-span-5">
                <h2
                  data-split-reveal
                  className={`font-bold uppercase ${t.heading} leading-[0.95] break-words`}
                  style={{
                    fontSize: "clamp(1.375rem, 5vw, 3.5rem)",
                    letterSpacing: "clamp(-0.4px, -0.12vw, -1.8px)",
                    ...t.headingGlow,
                  }}
                >
                  {packageSection.headline}
                </h2>
                {packageSection.subline && (
                  <p className={`mt-6 ${t.body} text-base md:text-lg leading-relaxed`}>
                    {packageSection.subline}
                  </p>
                )}
              </div>
              <div className="md:col-span-7">
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                  {packageSection.items.map((item, i) => (
                    <li
                      key={i}
                      data-split-reveal
                      className={`flex items-start gap-3 ${t.heading} text-base md:text-[17px] leading-snug py-3 border-b ${t.divider}`}
                    >
                      <span
                        aria-hidden
                        className="mt-2 inline-block w-2 h-2 rounded-full bg-[#4CAF50] shrink-0"
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                {packageSection.footnote && (
                  <p className={`mt-10 ${t.body} text-sm md:text-base leading-relaxed max-w-2xl`}>
                    {packageSection.footnote}
                  </p>
                )}
              </div>
            </div>
          </section>
        )}

        {/* 4. SOCIAL PROOF */}

        {(testimonials?.length || trustLine) && (
          <section className={`${t.sectionProof} py-36 md:py-40 px-6 md:px-10 lg:px-14`}>
            <div className="max-w-5xl mx-auto">
              <div className={`flex flex-wrap items-center gap-3 text-sm uppercase tracking-widest ${t.heading} mb-4`}>
                {showTrustStar && <span className="text-[#4CAF50]">★ 5,0 GOOGLE</span>}
                {trustLine && (
                  <span className={`${t.body} normal-case tracking-normal`}>
                    {showTrustStar ? "— " : ""}
                    {trustLine}
                  </span>
                )}
              </div>
              {testimonials && testimonials.length > 0 && (
                <TestimonialCarousel testimonials={testimonials} theme={theme} />
              )}
            </div>
          </section>
        )}

        {preManifestSection}

        {/* 5. HUGE-TITLE MANIFEST */}
        {manifest && (
          <section className={`${t.sectionManifest} py-36 md:py-40 px-6 md:px-10 lg:px-14`}>
            <div className="max-w-7xl mx-auto">
              <h2
                data-split-reveal
                className="font-bold uppercase text-white leading-[0.92] break-words whitespace-pre-line"
                style={{
                  letterSpacing: "clamp(-0.6px, -0.2vw, -2.8px)",
                  fontSize: "clamp(1.5rem, 7vw, 5rem)",
                  ...(isFuturistic ? { textShadow: "0 0 40px rgba(76,175,80,0.4)" } : {}),
                }}
              >
                {manifest}
              </h2>
            </div>
          </section>
        )}

        {/* 6. CASE STUDY TEASER */}
        {caseStudy && (
          <section className={`${t.sectionCase} py-36 md:py-40 px-6 md:px-10 lg:px-14`}>
            <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
              <div>
                <p className="text-[#4CAF50] uppercase text-xs md:text-sm tracking-widest mb-4 font-medium">
                  {caseStudy.label || "REFERENZ"}
                </p>
                <h2
                  data-split-reveal
                  className={`font-bold uppercase ${t.heading} leading-[0.95] break-words`}
                    style={{
                      fontSize: "clamp(1.125rem, 3.4vw, 2.25rem)",
                      letterSpacing: "clamp(-0.3px, -0.1vw, -1.4px)",
                      ...t.headingGlow,
                    }}
                >
                  {caseStudy.title}
                </h2>
                <p className={`mt-6 ${t.body} text-base md:text-lg leading-relaxed`}>
                  {caseStudy.text}
                </p>
                <p className={`mt-2 text-sm ${t.muted}`}>Emmerich am Rhein und Umgebung</p>
                <a
                  href={caseStudy.href || "#"}
                  className={`inline-flex items-center mt-8 px-6 py-3.5 rounded-full text-sm font-medium transition-all ${t.buttonPrimary}`}
                >
                  {caseStudy.linkLabel || "Mehr erfahren"} →
                </a>
              </div>
              <div className={`overflow-hidden aspect-[4/3] ${t.cardImg}`}>
                {caseStudy.image && (
                  <img
                    src={caseStudy.image}
                    alt={caseStudy.imageAlt || ""}
                    loading="lazy"
                    data-image-reveal
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
            </div>
          </section>
        )}

        {extraSection}

        {/* 7. FAQ */}
        {faq.length > 0 && (
          <section className={`${t.sectionFaq} py-36 md:py-40 px-6 md:px-10 lg:px-14`}>
            <div className="max-w-3xl mx-auto">
              <h2
                data-split-reveal
                className={`font-bold uppercase ${t.heading} leading-[0.95] mb-12 break-words`}
                style={{
                  fontSize: "clamp(1.25rem, 5vw, 3rem)",
                  letterSpacing: "clamp(-0.4px, -0.12vw, -2px)",
                  ...t.headingGlow,
                }}
              >
                Häufige Fragen
              </h2>
              <div className={`divide-y border-y ${t.divider}`}>
                {faq.map((item, i) => (
                  <details key={i} className="group py-6">
                    <summary className="flex items-center justify-between cursor-pointer list-none">
                      <span className={`${t.faqQuestion} font-medium text-base md:text-lg pr-6`}>
                        {item.frage}
                      </span>
                      <span className="text-[#4CAF50] text-2xl leading-none transition-transform group-open:rotate-45">
                        +
                      </span>
                    </summary>
                    <div className={`mt-4 ${t.body} leading-relaxed text-base`}>{item.antwort}</div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* 8. DARK CTA-PANEL */}
        {ctaText && (
          <section
            className="group relative py-36 md:py-40 px-6 md:px-10 lg:px-14 overflow-hidden transition-colors duration-500"
            style={{ backgroundColor: ctaBg }}
          >
            {/* Hover shimmer glow */}
            <div
              className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-out"
              aria-hidden="true"
            >
              <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] min-w-[800px] aspect-square rounded-full blur-3xl transition-transform duration-700 ease-out group-hover:scale-110"
                style={{
                  background: ctaIsGreen
                    ? "radial-gradient(circle, rgba(134,239,116,0.55) 0%, rgba(187,247,208,0.28) 35%, rgba(76,175,80,0) 70%)"
                    : "radial-gradient(circle, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.08) 40%, rgba(10,10,10,0) 70%)",
                }}
              />
            </div>
            {isFuturistic && !ctaIsGreen && (
              <div
                className="absolute inset-0 pointer-events-none opacity-25"
                style={{
                  backgroundImage:
                    "linear-gradient(rgba(76,175,80,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(76,175,80,0.12) 1px, transparent 1px)",
                  backgroundSize: "48px 48px",
                }}
              />
            )}
            <div className="relative max-w-5xl mx-auto text-center">
              <h2
                data-split-reveal
                className="font-bold uppercase text-white leading-[0.95] break-words"
                style={{
                  fontSize: "clamp(1.5rem, 6vw, 3.5rem)",
                  letterSpacing: "clamp(-0.5px, -0.15vw, -2px)",
                  ...(isFuturistic && !ctaIsGreen ? { textShadow: "0 0 32px rgba(76,175,80,0.55)" } : {}),
                }}
              >
                {ctaText.headline}
              </h2>
              {ctaText.subtext && (
                <p className="mt-6 text-white/85 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
                  {ctaText.subtext}
                </p>
              )}
              <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href={ctaText.buttonHref || "/kontakt"}
                  {...(ctaText.buttonExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  className={`inline-flex items-center px-8 py-4 rounded-full text-sm font-medium transition-all ${ctaButtonClass}`}
                >
                  {ctaText.buttonLabel || "Kostenlose Erstberatung"}
                </a>
                {ctaText.secondaryButtonLabel && (
                  <a
                    href={ctaText.secondaryButtonHref || "#"}
                    {...(ctaText.secondaryButtonExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    className="inline-flex items-center px-8 py-4 rounded-full text-sm font-medium transition-all bg-transparent text-white border border-white/40 hover:border-white hover:bg-white/5"
                  >
                    {ctaText.secondaryButtonLabel}
                  </a>
                )}
              </div>

            </div>
          </section>
        )}

        {postCtaSection}

        {/* 9. CROSS-LINK-TEASER */}
        {crossLink && (
          <section className={`${t.sectionCross} py-36 md:py-40 px-6 md:px-10 lg:px-14`}>
            <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
              <div className={`overflow-hidden aspect-[4/3] md:order-1 order-2 ${t.cardImg}`}>
                {crossLink.image && (
                  <img
                    src={crossLink.image}
                    alt={crossLink.imageAlt || ""}
                    loading="lazy"
                    data-image-reveal
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div className="md:order-2 order-1">
                <p className="text-[#4CAF50] uppercase text-xs md:text-sm tracking-widest mb-4 font-medium">
                  {crossLink.label || "Das könnte Sie auch interessieren"}
                </p>
                <h2
                  data-split-reveal
                  className={`font-bold uppercase ${t.heading} leading-[0.95] break-words`}
                  style={{
                    fontSize: crossLink.titleSize || "clamp(1.5rem, 6vw, 3.5rem)",
                    letterSpacing: "clamp(-0.5px, -0.15vw, -2px)",
                    ...t.headingGlow,
                  }}
                >
                  {crossLink.title}
                </h2>
                <p className={`mt-6 ${t.body} text-base md:text-lg leading-relaxed`}>
                  {crossLink.text}
                </p>
                <a
                  href={crossLink.href}
                  className={`inline-flex items-center mt-8 px-6 py-3.5 rounded-full text-sm font-medium transition-all ${t.buttonPrimary}`}
                >
                  Weiter lesen →
                </a>
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}
